import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import "./TaskVerification.css";
import "./TimeTrialGroupCard.css";

const truncateTask = (task = "", maxLength = 120) => {
  const text = String(task || "").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}…`;
};

const parseDate = (dateString) => {
  if (!dateString) return null;
  const normalized =
    typeof dateString === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateString) &&
    !/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(dateString)
      ? `${dateString}Z`
      : dateString;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLocalDateTime = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return "--";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
};

const formatDuration = (startDate, endDate) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (!start || !end) return "--";

  const diffInMinutes = Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
  if (diffInMinutes < 0) return "--";

  const days = Math.floor(diffInMinutes / (60 * 24));
  const hours = Math.floor((diffInMinutes % (60 * 24)) / 60);
  const minutes = diffInMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
  return parts.join(" ");
};

const isTimeTrialActive = (startDate, endDate) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (!start || !end) return false;
  const now = Date.now();
  return now >= start.getTime() && now < end.getTime();
};

export const TimeTrialGroupCard = ({ group, listBasePath }) => {
  const navigate = useNavigate();
  const verificationListPath =
    listBasePath || "/super-admin/manage-organization/task-verification";
  const timeTrialId = group?.timetrial?._id;
  const pendingCount = group?.counts?.pending || 0;
  const taskText = group?.timetrial?.task || "No task description";
  const startDate = group?.timetrial?.startDate;
  const endDate = group?.timetrial?.endDate;
  const isActive = isTimeTrialActive(startDate, endDate);
  const timeLabel = formatLocalDateTime(startDate);
  const durationLabel = formatDuration(startDate, endDate);

  const handleView = () => {
    if (!timeTrialId) return;
    navigate(`${verificationListPath}/time-trial/${timeTrialId}`);
  };

  return (
    <div className="task__card task__card--compact timetrial-group-card">
      <div className="timetrial-group-card__head">
        <div className="timetrial-group-card__body">
          <div className="timetrial-group-card__title-row">
            <h4 className="ftwt__b timetrial-group-card__title">
              {timeLabel}
              <span className="timetrial-group-card__duration">
                {" · "}
                {durationLabel}
              </span>
            </h4>
            {isActive ? (
              <span className="timetrial-group-card__active-flag">Active</span>
            ) : null}
          </div>
          <p className="timetrial-group-card__task">{truncateTask(taskText)}</p>
        </div>
      </div>
      <div className="timetrial-group-card__footer">
        <div className="timetrial-group-card__meta">
          <h6 className="ftwt__r">Pending submissions</h6>
          <h6 className="ftwt__r task__no">{pendingCount}</h6>
        </div>
        <button type="button" onClick={handleView} className="contained__lgbtn">
          View
        </button>
      </div>
    </div>
  );
};

TimeTrialGroupCard.propTypes = {
  group: PropTypes.shape({
    timetrial: PropTypes.shape({
      _id: PropTypes.string,
      task: PropTypes.string,
      startDate: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      endDate: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
    }),
    counts: PropTypes.shape({
      pending: PropTypes.number,
      approved: PropTypes.number,
      rejected: PropTypes.number,
    }),
  }).isRequired,
  listBasePath: PropTypes.string,
};
